import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import apiFeedbacks, { FeedbackMood } from 'api/feedbacks';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import translations from 'services/translations/translations';

import 'style/feedback/Feedback.css';

// The smileys in the order of the modal
export const FEEDBACK_MOODS: { mood: FeedbackMood; emoji: string }[] = [
  { mood: 'negative', emoji: '😞' },
  { mood: 'neutral', emoji: '😐' },
  { mood: 'positive', emoji: '😊' },
];

// Where the sad smiley is after each dodge: aside, then back, where it stays
const DODGES = [0, -150, 0];

// The overall rating, 1-10
const RATINGS = Array.from({ length: 10 }, (_, index) => index + 1);

// The thanks stays this long before the modal closes
const THANKS_MS = 2000;

/**
 * A button that opens the feedback modal, as in ProBuilder: a free text, a smiley and an
 * overall rating 1-10. The page is what the averages are counted by (e.g. "Laskenta"),
 * the location tells exactly where the feedback was given.
 */
const FeedbackButton = ({
  className,
  location,
  page,
}: {
  // E.g. feedback-button-header on the green top bar
  className?: string;
  location: string;
  page: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.common}:feedback.${key}`, options);

  // The version the user has: loaded with the page, so it is that of the open application
  const appBuiltAt: null | string | undefined = useSelector(
    (state: any) => state.settings.settings?.buildTime
  );
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [mood, setMood] = useState<FeedbackMood | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  // How many times the sad smiley has dodged since the modal was opened
  const [dodges, setDodges] = useState(0);
  const dodge = () => setDodges(Math.min(dodges + 1, DODGES.length - 1));

  const open = () => {
    setText('');
    setMood(null);
    setRating(null);
    setDodges(0);
    setSent(false);
    setFailed(false);
    setIsOpen(true);
  };

  useEffect(() => {
    if (!sent) {
      return undefined;
    }
    const timer = window.setTimeout(() => setIsOpen(false), THANKS_MS);
    return () => window.clearTimeout(timer);
  }, [sent]);

  const send = async () => {
    if (mood === null) {
      return;
    }
    setSending(true);
    setFailed(false);
    try {
      await apiFeedbacks.post({
        appBuiltAt: appBuiltAt ?? null,
        location,
        mood,
        page,
        rating,
        text: text.trim(),
      });
      setSent(true);
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        className={`feedback-button ${className ?? ''}`}
        onClick={open}
        title={tr('Give')}
        type="button"
      >
        <span aria-hidden="true">📣</span> {tr('Give')}
      </button>
      <Modal
        className="feedback-modal"
        isOpen={isOpen}
        toggle={() => setIsOpen(false)}
      >
        <ModalHeader toggle={() => setIsOpen(false)}>{tr('Title')}</ModalHeader>
        <ModalBody>
          {sent ? (
            <div className="feedback-thanks">
              <span aria-hidden="true">😄</span>
              <p>{tr('Thanks')}</p>
            </div>
          ) : (
            <>
              <p className="feedback-intro">{tr('Intro')}</p>
              <p className="feedback-page">
                {tr('About', {
                  page: location ? `${page} › ${location}` : page,
                })}
              </p>
              <textarea
                className="form-control"
                maxLength={4000}
                onChange={(event) => setText(event.target.value)}
                placeholder={tr('Placeholder')}
                rows={4}
                value={text}
              />
              <p className="feedback-choose">{tr('Choose')}</p>
              <div className="feedback-moods">
                {FEEDBACK_MOODS.map((choice) => (
                  <button
                    aria-label={tr(`moods.${choice.mood}`)}
                    aria-pressed={mood === choice.mood}
                    className={mood === choice.mood ? 'chosen' : undefined}
                    key={choice.mood}
                    onClick={() => setMood(choice.mood)}
                    // A little humour: the sad smiley dodges the pointer twice
                    onMouseEnter={
                      choice.mood === 'negative' ? dodge : undefined
                    }
                    style={
                      choice.mood === 'negative'
                        ? { transform: `translateX(${DODGES[dodges]}px)` }
                        : undefined
                    }
                    title={tr(`moods.${choice.mood}`)}
                    type="button"
                  >
                    {choice.emoji}
                  </button>
                ))}
              </div>
              <p className="feedback-choose">{tr('Overall rating')}</p>
              <div className="feedback-ratings">
                {RATINGS.map((value) => (
                  <button
                    aria-pressed={rating === value}
                    className={rating === value ? 'chosen' : undefined}
                    key={value}
                    // A second click takes the rating away
                    onClick={() => setRating(rating === value ? null : value)}
                    type="button"
                  >
                    {value}
                  </button>
                ))}
              </div>
              {failed ? (
                <p className="feedback-failed">{tr('Failed')}</p>
              ) : null}
            </>
          )}
        </ModalBody>
        {sent ? null : (
          <ModalFooter>
            <PrimaryButton disabled={mood === null || sending} onClick={send}>
              {tr('Send')}
            </PrimaryButton>
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};

export default FeedbackButton;
